import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import RouteIcon from "@mui/icons-material/AltRoute";
import TrainIcon from "@mui/icons-material/Train";
import PlaceIcon from "@mui/icons-material/Place";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import DirectionsTransitFilledIcon from "@mui/icons-material/DirectionsTransitFilled";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import RadarIcon from "@mui/icons-material/Radar";
import { useTranslation } from "../i18n/useTranslation";
import irrLogo from "../assets/irr.gif";
import { TRAIN_TYPE_ORDER } from "../data/staticNetwork";

const StatsPanel = ({ totals, typeBreakdown, trainTypeMeta, statusMetrics = {} }) => {
  const { t } = useTranslation();
  const breakdownEntries = [
    ...TRAIN_TYPE_ORDER.filter(
      (key) => typeof typeBreakdown?.[key] === "number" && typeBreakdown[key] > 0
    ).map((key) => [key, typeBreakdown[key]]),
    ...Object.entries(typeBreakdown || {}).filter(
      ([key, count]) =>
        !TRAIN_TYPE_ORDER.includes(key) && typeof count === "number" && count > 0
    ),
  ];
  const {
    online = 0,
    offline = 0,
    inStationsAll = 0,
    outStationsAll = 0,
    inStationsActive = 0,
    outStationsActive = 0,
  } = statusMetrics;

  const highlightTiles = [
    {
      icon: RouteIcon,
      label: t("stats.routes"),
      value: totals.routes,
      gradient: "linear-gradient(135deg, rgba(37,99,235,0.22), rgba(59,130,246,0.14))",
      accent: "#1d4ed8",
    },
    {
      icon: PlaceIcon,
      label: t("stats.stations"),
      value: totals.stations,
      gradient: "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(45,212,191,0.12))",
      accent: "#0f9d58",
    },
    {
      icon: TrainIcon,
      label: t("stats.trains"),
      value: totals.trains,
      gradient: "linear-gradient(135deg, rgba(245,158,11,0.24), rgba(251,191,36,0.14))",
      accent: "#d97706",
    },
  ];

  const connectivityMetrics = [
    {
      label: t("stats.online"),
      value: online,
      icon: CheckCircleOutlineIcon,
      accent: "#059669",
    },
    {
      label: t("stats.offline"),
      value: offline,
      icon: PowerSettingsNewIcon,
      accent: "#b91c1c",
    },
  ];

  const presenceGroups = [
    {
      label: t("stats.withOffline"),
      inValue: inStationsAll,
      outValue: outStationsAll,
      icon: MeetingRoomIcon,
      accent: "#1d4ed8",
    },
    {
      label: t("stats.withoutOffline"),
      inValue: inStationsActive,
      outValue: outStationsActive,
      icon: TravelExploreIcon,
      accent: "#6366f1",
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        background: "linear-gradient(160deg, rgba(241,245,249,0.95), rgba(226,232,240,0.7))",
        border: "1px solid rgba(15,23,42,0.08)",
      }}
    >
      <Stack spacing={3.5}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Box
            sx={{
              bgcolor: "#fff",
              border: "2px solid rgba(37,99,235,0.4)",
              borderRadius: 2,
              boxShadow: "0 12px 30px rgba(15,23,42,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1.5,
            }}
          >
            <Box component="img" src={irrLogo} alt={t("app.title")} sx={{ display: "block" }} />
          </Box>

          <Stack spacing={0.25}>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "0.01em" }}>
              {t("stats.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("stats.subtitle")}
            </Typography>
          </Stack>
        </Stack>

        <Grid container spacing={2.5}>
          {highlightTiles.map(({ icon: Icon, label, value, gradient, accent }) => (
            <Grid item xs={12} md={4} key={label}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.75,
                  borderRadius: 3,
                  background: gradient,
                  border: `1px solid ${accent}22`,
                  height: "100%",
                }}
              >
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: `${accent}26`,
                        color: accent,
                        width: 42,
                        height: 42,
                      }}
                    >
                      <Icon fontSize="medium" />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      {label}
                    </Typography>
                  </Stack>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    {value}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.25,
                borderRadius: 3,
                background: "linear-gradient(140deg, rgba(59,130,246,0.14), rgba(255,255,255,0.9))",
                border: "1px solid rgba(37,99,235,0.18)",
                height: "100%",
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.75} alignItems="center">
                  <Avatar
                    sx={{ bgcolor: "rgba(37,99,235,0.22)", color: "#1d4ed8", width: 52, height: 52 }}
                  >
                    <RadarIcon fontSize="medium" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                      {t("stats.connectivity")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("stats.subtitle")}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ borderColor: "rgba(37,99,235,0.18)" }} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  {connectivityMetrics.map(({ label, value, icon: Icon, accent }) => (
                    <Paper
                      key={label}
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: 2.5,
                        bgcolor: `${accent}15`,
                        border: `1px solid ${accent}33`,
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            sx={{ bgcolor: `${accent}1f`, color: accent, width: 34, height: 34 }}
                          >
                            <Icon fontSize="small" />
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {label}
                          </Typography>
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
                          {value}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.25,
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(14,165,233,0.16), rgba(56,189,248,0.12))",
                border: "1px solid rgba(14,165,233,0.2)",
                height: "100%",
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.75} alignItems="center">
                  <Avatar
                    sx={{ bgcolor: "rgba(14,165,233,0.22)", color: "#0284c7", width: 52, height: 52 }}
                  >
                    <DirectionsTransitFilledIcon fontSize="medium" />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    {t("stats.stationPresence")}
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  {presenceGroups.map(({ label, inValue, outValue, icon: Icon, accent }) => (
                    <Paper
                      key={label}
                      elevation={0}
                      sx={{
                        p: 2.25,
                        borderRadius: 2.5,
                        bgcolor: "rgba(255,255,255,0.9)",
                        border: `1px solid ${accent}2b`,
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            sx={{ bgcolor: `${accent}1f`, color: accent, width: 36, height: 36 }}
                          >
                            <Icon fontSize="small" />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {label}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={2.5}>
                          <Stack spacing={0.5}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
                              {inValue}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("stats.inStations")}
                            </Typography>
                          </Stack>
                          <Stack spacing={0.5}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
                              {outValue}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("stats.outStations")}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Divider flexItem>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {t("stats.fleetComposition")}
          </Typography>
        </Divider>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
          {breakdownEntries.map(([typeId, count]) => {
            const meta = trainTypeMeta[typeId];
            if (!meta) return null;
            return (
              <Chip
                key={typeId}
                label={`${meta.label} · ${count}`}
                icon={<TrainIcon sx={{ color: meta.color }} />}
                sx={{
                  bgcolor: meta.accent,
                  color: meta.color,
                  px: 1.5,
                  py: 1,
                  fontWeight: 600,
                  "& .MuiChip-icon": { color: meta.color },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default StatsPanel;
